import React, {
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
  type HTMLAttributes,
} from 'react';
import classNames from 'classnames';
import isArray from 'lodash/isArray';

import { selectPhoto } from '../services/photos.service';
import { broadcastClientEvent } from '@/modules/core/client/services/client-runtime';

/**
 * @param {string[]|string} names - array of names or a single name
 *
 * @returns {string} the provided name or randomly picked name from array
 */
function selectName(names: string | string[]): string {
  return Array.isArray(names)
    ? (names[Math.floor(Math.random() * names.length)] as string)
    : names;
}

/**
 * Partially migrated tr-boards directive
 * modules/core/client/directives/tr-boards.client.directive.js
 *
 * @TODO implement primary, inset, error and maybe other attributes, which are currently board classes
 *  and which could become attributes <Board primary inset error names="bokeh" />
 */
type BoardProps = Omit<HTMLAttributes<HTMLElement>, 'style'> & {
  names?: string | string[];
  ignoreBackgroundOnSmallScreen?: boolean;
  style?: CSSProperties | null;
  children?: ReactNode;
};

export default function Board({
  names = 'bokeh',
  ignoreBackgroundOnSmallScreen = false,
  style = null,
  children,
  className,
  ...rest
}: BoardProps) {
  const [photo, setPhoto] = useState<ReturnType<typeof selectPhoto> | null>(
    null,
  );

  useEffect(() => {
    // pick a name from provided names
    const selectedName = selectName(names);
    // select an appropriate photo by name
    const photo = selectPhoto(selectedName);
    const photoObject = { [selectedName]: photo };

    // update the state with the selected photo
    setPhoto(photo);

    // inform the parent that the photo is displayed
    // ...useful e.g. for displaying photo credits elsewere
    broadcastClientEvent('photoCreditsUpdated', photoObject);

    // inform the parent that the photo is not displayed anymore
    return () => {
      broadcastClientEvent('photoCreditsRemoved', photoObject);
    };
  }, [isArray(names) ? names.join(' ') : names]);

  if (photo) {
    if (style) {
      style.backgroundImage = `url("${photo.imageUrl}")`;
    } else {
      style = { backgroundImage: `url("${photo.imageUrl}")` };
    }
  }
  const addedClasses = ['board'];
  if (ignoreBackgroundOnSmallScreen) {
    addedClasses.push('small-screen-bad-background');
  }
  return (
    <section
      style={{ ...style }}
      className={classNames(addedClasses, className)}
      {...rest}
    >
      {children}
    </section>
  );
}
